import type { BlockMatchSample, PreviewTypeRemovalResponse, ProfileTypeStats, TypeRemoval } from '@shared/schemas';

import type { LoadStatus, ProfileTypeBlock } from '../domain/profile-type.model';

/**
 * Transient result of the last settled operation. The page turns `success` into a
 * success snackbar and `error` into an error one, then clears it. One bucket is
 * enough: the form and the row actions are disabled while a save is in flight.
 */
export interface Notice {
  success: string | null;
  error: string | null;
  /**
   * The bulk removal a success is about (#652): the snackbar offers Undo on it. Absent
   * for every other notice.
   */
  undoRemovalId?: string | null;
}

/**
 * Redux state for the admin Profile types page. The slice owns the loaded types,
 * the initial-load lifecycle, the save/delete busy flags and the last operation's
 * notice.
 *
 * What it deliberately does NOT own: the editor's draft. A half-typed block is not
 * application state; nothing outside the form reads it, and putting every
 * keystroke through the store would buy a re-render of the list for each one. The
 * page holds the draft locally and hands it over on save, which is the same split
 * the sibling slices make for their dialogs.
 */
/**
 * The count behind the apply dialog and the editor: how many profiles the block
 * would tag right now.
 *
 * `failed` is its own state rather than a count of 0. A preview that could not be
 * computed and a rule that matches nobody lead to opposite decisions (one is "try
 * again", the other is "your rule is too narrow") and collapsing them into 0 would
 * present a broken request as a finding about the data.
 */
export interface RunPreview {
  status: 'idle' | 'loading' | 'loaded' | 'failed';
  count: number;
  /**
   * A few of the profiles that would be tagged.
   *
   * The count is not a check an admin can act on. A rule that picked up an
   * over-broad keyword reports a plausible number, and the mistake shows only in
   * who it selected; "Accounting manager" in a list of family-office executives
   * is obvious, "15" is not. Since tagging has no undo, these names are the last
   * place to catch it.
   */
  sample: BlockMatchSample[];
  /**
   * Matches that already carry the tag.
   *
   * Only read when `count` is 0, where it settles which of the two zeroes this is:
   * a rule nobody matches, or a rule already applied. They call for opposite
   * actions, and a rule that has been run once always produces the second.
   */
  alreadyTagged: number;
  /**
   * For each condition, the profiles the block would match without it ("Without it:
   * 2,594 profiles"), tag or no tag. Empty until a count has landed.
   */
  withoutCounts: number[];
}

/**
 * The type priority (#554): the whitelist in the order that decides which type a
 * profile SHOWS when it carries several.
 *
 * `names` is the working copy; reordering is local until "Save order", because
 * the save recomputes every profile and firing it per arrow-click would be a
 * full-table UPDATE per keystroke. `saved` is what the server last confirmed, and
 * the two differing is exactly what enables the button.
 */
export interface TypePriorityState {
  names: string[];
  saved: string[];
  status: LoadStatus;
  saving: boolean;
}

/**
 * The bulk removal dialog (#652): which type it is open on and what removing would do.
 * `failed` is its own state for the same reason as `RunPreview`'s; a preview that
 * could not be computed is not a type nobody carries.
 */
export interface RemovalState {
  target: string | null;
  status: 'idle' | 'loading' | 'loaded' | 'failed';
  preview: PreviewTypeRemovalResponse | null;
  removing: boolean;
}

export interface ProfileTypesState {
  blocks: ProfileTypeBlock[];
  loadStatus: LoadStatus;
  saving: boolean;
  /** Name of the type whose block is being removed, so only its row shows a spinner. */
  clearingName: string | null;
  /** Types whose Automatic switch is in flight, so their switch alone is disabled. */
  togglingNames: string[];
  /** Name of the type being applied, so only its row shows a spinner. */
  runningName: string | null;
  runPreview: RunPreview;
  /**
   * The overview's figures: coverage, profiles per type, fill rate per field. `null`
   * until loaded, and left `null` if they fail: the page works without them, it only
   * loses its numbers.
   */
  stats: ProfileTypeStats | null;
  /**
   * The editor's live count; the same shape, asked of a block not saved yet.
   *
   * Its own field rather than a shared one with `runPreview`: the two answer
   * different blocks, and a single bucket would show the editor the count the
   * overview last asked for.
   *
   * It goes back to `loading` on every edit of the draft. A count is only true of the
   * keywords it was asked about, and a stale one beside a changed block is worse than
   * no count at all; it is the number an admin would save on.
   */
  draftPreview: RunPreview;
  typePriority: TypePriorityState;
  /**
   * The type whose apply dialog should open; set by "Review and apply" once its save
   * has landed, so the dialog always applies the block as stored, never the draft.
   */
  applyTarget: string | null;
  removal: RemovalState;
  /** "Recent removals"; empty until loaded, and left empty if that fails. */
  removals: TypeRemoval[];
  /** The removal being undone, so only its row shows a spinner. */
  undoingId: string | null;
  notice: Notice;
}

export const emptyNotice: Notice = { success: null, error: null };
export const emptyTypePriority: TypePriorityState = {
  names: [],
  saved: [],
  status: 'idle',
  saving: false,
};
export const closedRemoval: RemovalState = { target: null, status: 'idle', preview: null, removing: false };
export const idlePreview: RunPreview = { status: 'idle', count: 0, sample: [], alreadyTagged: 0, withoutCounts: [] };

export const initialState: ProfileTypesState = {
  blocks: [],
  loadStatus: 'idle',
  saving: false,
  clearingName: null,
  togglingNames: [],
  runningName: null,
  runPreview: idlePreview,
  stats: null,
  draftPreview: idlePreview,
  typePriority: emptyTypePriority,
  applyTarget: null,
  removal: closedRemoval,
  removals: [],
  undoingId: null,
  notice: emptyNotice,
};
